import { Supertype, supertypeClass, property, remote, Remoteable, Persistable } from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { Publisher } from '@hit/publisher';
import { HitAppCtx } from '../orca/HitAppCtx';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';

@supertypeClass({ toClient: false, toServer: true })
export class RabbitmqPublisher extends Everything {
  @property({ isLocal: true })
  private publisher: Publisher;

  @property({ isLocal: true })
  private static _mqConfig;

  @property({ isLocal: true })
  private static _singleton: RabbitmqPublisher = null;

  static singleton() {
    return RabbitmqPublisher._singleton || (RabbitmqPublisher._singleton = RabbitmqPublisher.new());
  }

  constructor() {
    super();
  }

  static new() {
    return new RabbitmqPublisher().init();
  }

  setMQConfig(mqConfig) {
    RabbitmqPublisher._mqConfig = mqConfig;
  }

  get logger() {
    return LoggerSingleton.getInstance();
  }

  init() {
    if (RabbitmqPublisher._singleton) {
      throw new Error('Use RabbitmqPublisher.singleton instead.');
    }
    return this;
  }

  start() {
    if (!RabbitmqPublisher._mqConfig) {
      throw new Error('Please set mgConfig before start.');
    }
    if (!this.publisher && this.amorphic.config.nconf.get('genericPublisherEnabled')) {
      return this._startPublisher();
    }
  }

  publish(data, schema) {
    if (this.publisher) {
      if (!data.channel || !data.eventType) {
        this.logger.error(
          {
            module: 'RabbitmqPublisher',
            function: 'publish',
            category: Category.request,
          },
          'No channel or event type in the data, message dropped...'
        );
        return;
      }
      const routingKey = data.channel + '.' + data.eventType;
      try {
        return this.publisher.publish(data, schema, RabbitmqPublisher._mqConfig.exchange, routingKey);
      } catch (e) {
        this.logger.error(
          {
            module: 'RabbitmqPublisher',
            function: 'publish',
            category: Category.request,

            error: {
              name: e?.name,
              message: e?.message,
              stack: e?.stack,
              isHumanRelated: false,
            },
          },
          'Rabbitmq Publisher has an error',
          e
        );
      }
    } else {
      // If publisher was not started, start it now and publish once connected.
      // Set defaults if no config has been set.
      if (!RabbitmqPublisher._mqConfig && this.amorphic.config) {
        const rabbitmqHitUserEventsConfig = this.amorphic.config.nconf.get('rabbitmqHitUserEventsConfig');
        rabbitmqHitUserEventsConfig.host = this.amorphic.config.nconf.get('RABBIT_HOST');
        rabbitmqHitUserEventsConfig.port = this.amorphic.config.nconf.get('RABBIT_PORT');
        rabbitmqHitUserEventsConfig.user = this.amorphic.config.nconf.get('RABBIT_USERNAME');
        rabbitmqHitUserEventsConfig.pswd = this.amorphic.config.nconf.get('RABBIT_PASSWORD');
        rabbitmqHitUserEventsConfig.secure = this.amorphic.config.nconf.get('RABBIT_SECURE') || false;

        RabbitmqPublisher.singleton().setMQConfig(rabbitmqHitUserEventsConfig);
      }

      if (this.amorphic.config.nconf.get('genericPublisherEnabled')) {
        return this._startPublisher(() => this.publish(data, schema));
      }
    }
  }

  _startPublisher(onPublisherConnect?: () => void) {
    const connectString =
      `amqp${RabbitmqPublisher._mqConfig.secure ? 's' : ''}://` +
      RabbitmqPublisher._mqConfig.user +
      ':' +
      RabbitmqPublisher._mqConfig.pswd +
      '@' +
      RabbitmqPublisher._mqConfig.host +
      ':' +
      RabbitmqPublisher._mqConfig.port +
      '?heartbeat=' +
      RabbitmqPublisher._mqConfig.heartbeat;

    this.logger.debug(
      {
        module: 'RabbitmqPublisher',
        function: '_startPublisher',
        category: Category.request,
      },
      'Connection String: ' + connectString.replace(/:.*@/, '://*****:*****@')
    );

    const publisherConfig = {
      exchange: RabbitmqPublisher._mqConfig.exchange,
      persistent: RabbitmqPublisher._mqConfig.persistent,
      connectRetryTotal: RabbitmqPublisher._mqConfig.connectRetryTotal,
      connectRetryInterval: RabbitmqPublisher._mqConfig.connectRetryInterval,
      publishRetryTotal: RabbitmqPublisher._mqConfig.publishRetryTotal,
      publishRetryInterval: RabbitmqPublisher._mqConfig.publishRetryInterval,
    };

    this.publisher = new Publisher(this.logger, publisherConfig);

    this.publisher.on(
      'error',
      function (error) {
        this.logger.error(
          {
            module: 'RabbitmqPublisher',
            function: '_startPublisher',
            category: Category.request,

            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
            },
          },
          'Rabbitmq Publisher has an error'
        );
      }.bind(this)
    );

    this.publisher.on(
      'connected',
      function () {
        this.logger.info(
          {
            module: 'RabbitmqPublisher',
            function: '_startPublisher',
            category: Category.request,
          },
          'Rabbitmq Publisher is connected.'
        );
        if (typeof onPublisherConnect === 'function') {
          onPublisherConnect();
        }
      }.bind(this)
    );

    this.logger.info(
      {
        module: 'RabbitmqPublisher',
        function: '_startPublisher',
        category: Category.request,
      },
      'Rabbitmq Publisher is connecting...'
    );
    return this.publisher.connect(connectString);
  }
}
